'use client';

/**
 * Featured game tile with lazy-mounted looping video and static poster fallback.
 */

import {useEffect, useRef, useState} from 'react';
import Image from 'next/image';
import type {WebArcadeGame} from '@/services/web-arcade/types';
import {trackArcadeEvent, ARCADE_EVENT} from './trackArcadeEvent';

interface FeaturedGameTileProps {
  game: WebArcadeGame;
  rank?: number;
}

const FeaturedGameTile = ({game, rank = 0}: FeaturedGameTileProps) => {
  const {gameId, title, featuredImage, featuredVideo} = game;
  const containerRef = useRef<HTMLAnchorElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [isInView, setIsInView] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  // Sticky — once a video fails we unmount it so the browser stops retrying.
  const [hasVideoError, setHasVideoError] = useState(false);

  // Lazy-mount: disconnect after first intersection so the video stays alive.
  useEffect(() => {
    if (!featuredVideo) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }
    const node = containerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {rootMargin: '200px 0px', threshold: 0.01}
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [featuredVideo]);

  // Pause when the tab is hidden to save battery / bandwidth.
  useEffect(() => {
    if (!isInView) return;
    const onVisibility = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) {
        v.pause();
      } else {
        v.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isInView]);

  return (
    <a
      ref={containerRef}
      href={`/games/${gameId}/`}
      aria-label={title}
      onClick={() =>
        trackArcadeEvent(ARCADE_EVENT.TILE_CLICKED, {
          result: JSON.stringify({game_id: gameId, rank, section: 'featured'}),
        })
      }
      className='
        group relative block w-full overflow-hidden rounded-2xl
        shadow-md transition-transform duration-200
        outline-none focus-visible:ring-2 focus-visible:ring-primary-blue
        focus-visible:ring-offset-2
        hover:scale-[1.01] active:scale-[0.99]
      '
    >
      {/* 3:2 portrait ratio matching the mobile FeatureGameList tile. */}
      <div className='relative aspect-[150/225] w-full bg-tile-dark'>
        {featuredImage ? (
          <Image
            src={featuredImage}
            alt={title}
            fill
            sizes='(max-width: 600px) 50vw, 300px'
            priority={false}
            className={`object-cover transition-opacity duration-300 ${
              isVideoReady && !hasVideoError ? 'opacity-0' : 'opacity-100'
            }`}
          />
        ) : (
          <div className='absolute inset-0 bg-tile-dark' aria-hidden='true' />
        )}

        {isInView && featuredVideo && !hasVideoError ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            ref={videoRef}
            src={featuredVideo}
            {...(featuredImage ? {poster: featuredImage} : {})}
            autoPlay
            muted
            loop
            playsInline
            preload='metadata'
            onCanPlay={() => setIsVideoReady(true)}
            onError={() => {
              setHasVideoError(true);
              setIsVideoReady(false);
            }}
            className='absolute inset-0 h-full w-full object-cover'
          />
        ) : null}

        <div
          className='
            pointer-events-none absolute inset-x-0 bottom-0 h-2/5
            bg-gradient-to-t from-black/80 via-black/40 to-transparent
          '
          aria-hidden='true'
        />
        <div className='absolute inset-x-0 bottom-0 px-3 pb-3 sm:px-4 sm:pb-4'>
          <span
            className='
              line-clamp-2 text-[14px] font-extrabold leading-[18px] text-white
              drop-shadow-md sm:text-[16px] sm:leading-[20px]
            '
          >
            {title}
          </span>
        </div>
      </div>
    </a>
  );
};

export default FeaturedGameTile;
